import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import PageLayout from './PageLayout';
import { theme, t } from '../theme';
import { norm } from '../utils';
import {
  FaBox, FaBoxOpen, FaCubes, FaExclamationTriangle, FaArrowRight,
  FaChartPie, FaSearch
} from 'react-icons/fa';

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;

const Inventario = () => {
  const [produtos, setProdutos] = useState([]);
  const [semControle, setSemControle] = useState([]);
  const [embalagens, setEmbalagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const navigate = useNavigate();

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const [resP, resSC, resE] = await Promise.all([
        api.get('/produtos'),
        api.get('/produtos-sem-controle'),
        api.get('/api/embalagens'),
      ]);
      setProdutos(resP.data || []);
      setSemControle(resSC.data || []);
      setEmbalagens(resE.data || []);
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <PageLayout title="Inventário"><div style={{ textAlign: 'center', padding: 80, color: theme.colors.textMuted }}>Carregando...</div></PageLayout>;

  // Stats - Produtos com controle
  const totalProdutos = produtos.length;
  const produtosBaixo = produtos.filter(p => p.estoque_atual <= p.estoque_minimo && p.estoque_minimo > 0);
  const produtosZerados = produtos.filter(p => p.estoque_atual <= 0);
  const valorEstoque = produtos.reduce((s, p) => {
    const custo = p.custo_medio || p.custo_fixo || 0;
    return s + (custo * (p.estoque_atual || 0));
  }, 0);

  // Stats - Sem controle
  const totalSemControle = semControle.length;
  const custoTotalSC = semControle.reduce((s, p) => s + (p.custo_fixo || 0), 0);

  // Stats - Embalagens
  const totalEmbalagens = embalagens.length;
  const embalagensComEstoque = embalagens.filter(e => e.controlar_estoque);
  const embalagensBaixo = embalagensComEstoque.filter(e => e.estoque_atual <= e.estoque_minimo && e.estoque_minimo > 0);
  const valorEmbalagens = embalagensComEstoque.reduce((s, e) => s + (e.custo_unitario * e.estoque_atual), 0);

  // Busca global
  const buscaNorm = norm(busca);
  const produtosFiltrados = busca ? produtos.filter(p => norm(p.nome).includes(buscaNorm)) : [];
  const scFiltrados = busca ? semControle.filter(p => norm(p.nome).includes(buscaNorm)) : [];
  const embFiltrados = busca ? embalagens.filter(e => norm(e.nome).includes(buscaNorm)) : [];
  const temResultados = busca && (produtosFiltrados.length > 0 || scFiltrados.length > 0 || embFiltrados.length > 0);

  return (
    <PageLayout title="Inventário" subtitle="Visão geral de todos os estoques">

      {/* Cards resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>

        {/* Produtos com controle */}
        <div style={{ ...t.card, padding: '20px 24px', cursor: 'pointer' }} onClick={() => navigate('/estoque')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: theme.colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaBox size={18} color={theme.colors.primary} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>Produtos</div>
                <div style={{ fontSize: 12, color: theme.colors.textMuted }}>Com controle de estoque</div>
              </div>
            </div>
            <FaArrowRight size={12} color={theme.colors.textMuted} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Stat label="Total" valor={totalProdutos} />
            <Stat label="Valor em estoque" valor={fmt(valorEstoque)} isText />
            <Stat label="Estoque baixo" valor={produtosBaixo.length} cor={produtosBaixo.length > 0 ? theme.colors.warning : null} />
            <Stat label="Zerados" valor={produtosZerados.length} cor={produtosZerados.length > 0 ? theme.colors.danger : null} />
          </div>
        </div>

        {/* Sem controle */}
        <div style={{ ...t.card, padding: '20px 24px', cursor: 'pointer' }} onClick={() => navigate('/produtos-sem-controle')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: theme.colors.infoLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaBoxOpen size={18} color={theme.colors.info} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>Sem Controle</div>
                <div style={{ fontSize: 12, color: theme.colors.textMuted }}>Produtos sem estoque</div>
              </div>
            </div>
            <FaArrowRight size={12} color={theme.colors.textMuted} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Stat label="Total" valor={totalSemControle} />
            <Stat label="Custo total fixo" valor={fmt(custoTotalSC)} isText />
          </div>
        </div>

        {/* Embalagens */}
        <div style={{ ...t.card, padding: '20px 24px', cursor: 'pointer' }} onClick={() => navigate('/embalagens')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: theme.colors.successLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaCubes size={18} color={theme.colors.success} />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>Embalagens</div>
                <div style={{ fontSize: 12, color: theme.colors.textMuted }}>{embalagensComEstoque.length} com controle</div>
              </div>
            </div>
            <FaArrowRight size={12} color={theme.colors.textMuted} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Stat label="Total itens" valor={totalEmbalagens} />
            <Stat label="Valor em estoque" valor={fmt(valorEmbalagens)} isText />
            <Stat label="Estoque baixo" valor={embalagensBaixo.length} cor={embalagensBaixo.length > 0 ? theme.colors.warning : null} />
          </div>
        </div>
      </div>

      {/* Busca global */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <FaSearch style={{ position: 'absolute', left: 14, top: 13, color: theme.colors.textMuted }} />
        <input placeholder="Buscar em todos os estoques..." value={busca} onChange={e => setBusca(e.target.value)} style={{ ...t.input, paddingLeft: 40 }} />
      </div>

      {/* Resultados da busca */}
      {busca && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {produtosFiltrados.length > 0 && (
            <ResultSection title="Produtos com Controle" items={produtosFiltrados} renderItem={p => (
              <div key={p.id} style={rowStyle}>
                <div><div style={{ fontWeight: 500 }}>{p.nome}</div><div style={{ fontSize: 12, color: theme.colors.textMuted }}>{p.codigo_barras}</div></div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600, color: p.estoque_atual <= p.estoque_minimo && p.estoque_minimo > 0 ? theme.colors.danger : theme.colors.text }}>{p.estoque_atual} un</div>
                  <div style={{ fontSize: 12, color: theme.colors.textMuted }}>mín: {p.estoque_minimo}</div>
                </div>
              </div>
            )} />
          )}
          {scFiltrados.length > 0 && (
            <ResultSection title="Sem Controle" items={scFiltrados} renderItem={p => (
              <div key={p.id} style={rowStyle}>
                <div><div style={{ fontWeight: 500 }}>{p.nome}</div><div style={{ fontSize: 12, color: theme.colors.textMuted }}>{p.unidade_peso}</div></div>
                <div style={{ fontWeight: 500, color: theme.colors.textSecondary }}>{p.custo_fixo ? fmt(p.custo_fixo) : '-'}</div>
              </div>
            )} />
          )}
          {embFiltrados.length > 0 && (
            <ResultSection title="Embalagens" items={embFiltrados} renderItem={e => (
              <div key={e.id} style={rowStyle}>
                <div><div style={{ fontWeight: 500 }}>{e.nome}</div><div style={{ fontSize: 12, color: theme.colors.textMuted }}>{e.categoria || 'Sem categoria'}</div></div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 500 }}>{fmt(e.custo_unitario)}</div>
                  {e.controlar_estoque && <div style={{ fontSize: 12, color: e.estoque_atual <= e.estoque_minimo && e.estoque_minimo > 0 ? theme.colors.danger : theme.colors.textMuted }}>{e.estoque_atual} un</div>}
                </div>
              </div>
            )} />
          )}
          {!temResultados && (
            <div style={{ textAlign: 'center', padding: 40, color: theme.colors.textMuted }}>Nenhum item encontrado para "{busca}"</div>
          )}
        </div>
      )}

      {/* Alertas (quando não tem busca) */}
      {!busca && (produtosBaixo.length > 0 || embalagensBaixo.length > 0) && (
        <div style={{ ...t.card, padding: '20px 24px' }}>
          <h3 style={{ ...t.h3, marginBottom: 16 }}>
            <FaExclamationTriangle style={{ marginRight: 8, color: theme.colors.warning }} />
            Itens com Estoque Baixo
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {produtosBaixo.map(p => (
              <div key={p.id} style={{ ...rowStyle, background: theme.colors.warningLight, borderRadius: 10, padding: '10px 14px', cursor: 'pointer' }} onClick={() => navigate('/estoque')}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{p.nome}</div>
                  <div style={{ fontSize: 12, color: theme.colors.textMuted }}>Produto · Mín: {p.estoque_minimo}</div>
                </div>
                <span style={{ fontSize: 18, fontWeight: 700, color: p.estoque_atual <= 0 ? theme.colors.danger : theme.colors.warning }}>{p.estoque_atual}</span>
              </div>
            ))}
            {embalagensBaixo.map(e => (
              <div key={e.id} style={{ ...rowStyle, background: theme.colors.dangerLight, borderRadius: 10, padding: '10px 14px', cursor: 'pointer' }} onClick={() => navigate('/embalagens')}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{e.nome}</div>
                  <div style={{ fontSize: 12, color: theme.colors.textMuted }}>Embalagem · Mín: {e.estoque_minimo}</div>
                </div>
                <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.danger }}>{e.estoque_atual}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visão geral quando sem busca e sem alertas */}
      {!busca && produtosBaixo.length === 0 && embalagensBaixo.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: theme.colors.textMuted }}>
          <FaChartPie size={36} style={{ marginBottom: 12, color: theme.colors.border }} />
          <p style={{ fontSize: 15 }}>Todos os estoques estão em dia</p>
        </div>
      )}
    </PageLayout>
  );
};

const Stat = ({ label, valor, cor, isText }) => (
  <div>
    <div style={{ fontSize: 12, color: theme.colors.textMuted, marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: isText ? 15 : 20, fontWeight: 700, color: cor || theme.colors.text }}>{valor}</div>
  </div>
);

const ResultSection = ({ title, items, renderItem }) => (
  <div style={{ ...t.card, overflow: 'hidden' }}>
    <div style={{ padding: '12px 20px', background: theme.colors.bgSection, borderBottom: `1px solid ${theme.colors.borderLight}` }}>
      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title} ({items.length})
      </h4>
    </div>
    <div style={{ padding: '8px 20px' }}>
      {items.map(renderItem)}
    </div>
  </div>
);

const rowStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '10px 0', borderBottom: `1px solid ${theme.colors.borderLight}`,
};

export default Inventario;
